import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Link as MuiLink,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  GavelOutlined,
  LocationOnOutlined,
  ReceiptLongOutlined,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import {
  getMyTimeline,
  timelineErrMsg,
  TimelineEvent,
  TimelineProperty,
} from "../../Services/timelineApi";

// Record = the account-area blue (matches MyAccount); Permit = the Maintenance
// tab teal (matches PermitPanel). Two kinds, two colors — the reader tells them
// apart at a glance.
const RECORD_COLOR = "#1565C0";
const PERMIT_COLOR = "#004B4C";

type TypeFilter = "all" | "record" | "permit";

// The backend emits bare "YYYY-MM-DD" dates as UTC midnight, so render AND
// group in UTC — otherwise a homeowner west of UTC sees every work/permit date
// a day early (and occasionally in the wrong year bucket).
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const fmtMoney = (n: number | null) =>
  n == null ? null : `$${n.toLocaleString()}`;

/** One timeline row: a colored marker on the rail + a bordered content card. */
const EventRow = ({
  event,
  showProperty,
  onOpenProperty,
  isLast,
}: {
  event: TimelineEvent;
  showProperty: boolean;
  onOpenProperty: (p: TimelineProperty) => void;
  isLast: boolean;
}) => {
  const isRecord = event.kind === "record";
  const color = isRecord ? RECORD_COLOR : PERMIT_COLOR;
  const money = fmtMoney(event.amount);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      {/* Rail: icon marker + connecting line */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isRecord ? (
            <ReceiptLongOutlined fontSize="small" />
          ) : (
            <GavelOutlined fontSize="small" />
          )}
        </Box>
        {!isLast && (
          <Box sx={{ flex: 1, width: "2px", bgcolor: "#e0e0e0", mt: 0.5, minHeight: 16 }} />
        )}
      </Box>

      {/* Content card */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          mb: 2.5,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
          borderLeft: `4px solid ${color}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {event.title}
            </Typography>
            {event.subtitle && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.25 }}
              >
                {event.subtitle}
              </Typography>
            )}
          </Box>
          {money && (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color, whiteSpace: "nowrap" }}
            >
              {money}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5 }}
        >
          <Chip
            label={isRecord ? "Record" : "Permit"}
            size="small"
            sx={{ bgcolor: color, color: "#fff", height: 22, fontSize: 11, fontWeight: 600 }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {event.dateLabel} {fmtDate(event.date)}
          </Typography>
          {event.statusLabel && (
            <Chip
              label={event.statusLabel}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: 11 }}
            />
          )}
          {event.recordId && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              #{event.recordId}
            </Typography>
          )}
          {event.permitNumber && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              #{event.permitNumber}
            </Typography>
          )}
          {event.sourceUrl && (
            <MuiLink
              href={event.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12, color: PERMIT_COLOR }}
            >
              View source
            </MuiLink>
          )}
        </Stack>

        {showProperty && event.property && (
          <Chip
            icon={<LocationOnOutlined sx={{ fontSize: 16 }} />}
            label={event.property.address}
            size="small"
            onClick={() => onOpenProperty(event.property as TimelineProperty)}
            sx={{ mt: 1.5, maxWidth: "100%", cursor: "pointer" }}
          />
        )}
      </Box>
    </Box>
  );
};

const HomeHistory = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [properties, setProperties] = useState<TimelineProperty[]>([]);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [propFilter, setPropFilter] = useState<string>("all");

  // Guard against out-of-order responses and post-unmount state writes: only
  // the newest in-flight request applies its result (matters on the "Try
  // again" retry path, which can fire a second load while the first is pending).
  const reqRef = useRef(0);
  const mountedRef = useRef(true);
  // Set true on setup (not just at init): StrictMode's dev double-mount runs
  // setup → cleanup → setup on one instance without resetting the ref, which
  // would otherwise leave the guard stuck false and freeze the page loading.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const myReq = ++reqRef.current;
    setLoading(true);
    setError(null);
    getMyTimeline()
      .then((res) => {
        if (!mountedRef.current || reqRef.current !== myReq) return;
        setEvents(res.events);
        setProperties(res.properties);
      })
      .catch((e) => {
        if (!mountedRef.current || reqRef.current !== myReq) return;
        setError(timelineErrMsg(e, "We couldn't load your home history."));
      })
      .finally(() => {
        if (mountedRef.current && reqRef.current === myReq) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Straight to that home's Home History tab — the per-property view of
  // what this page shows.
  const openProperty = (p: TimelineProperty) =>
    navigate(`/${p.propertyId}/property-id/${p.propertyId}?tab=home-history`);

  // Filter → group by year (events arrive newest-first, so order is preserved).
  const groups = useMemo(() => {
    const filtered = events.filter(
      (e) =>
        (typeFilter === "all" || e.kind === typeFilter) &&
        (propFilter === "all" || e.property?.propertyId === propFilter)
    );
    const byYear = new Map<string, TimelineEvent[]>();
    for (const e of filtered) {
      const d = new Date(e.date);
      const y = Number.isNaN(d.getTime()) ? "—" : String(d.getUTCFullYear());
      if (!byYear.has(y)) byYear.set(y, []);
      byYear.get(y)!.push(e);
    }
    return Array.from(byYear.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [events, typeFilter, propFilter]);

  const totalFiltered = groups.reduce((n, [, list]) => n + list.length, 0);
  const recordCount = events.filter((e) => e.kind === "record").length;
  const permitCount = events.filter((e) => e.kind === "permit").length;

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #42a5f5 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 7 },
            mb: { xs: 3, md: 5 },
            mt: 4,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
            }}
          >
            Home History
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#fff", opacity: 0.9, mt: 1, maxWidth: 640 }}
          >
            Everything that's happened across your properties — confirmed records
            and matched permits, in one timeline.
          </Typography>
          {!loading && !error && events.length > 0 && (
            <Typography variant="body2" sx={{ color: "#fff", opacity: 0.85, mt: 2 }}>
              {recordCount} {recordCount === 1 ? "record" : "records"} · {permitCount}{" "}
              {permitCount === 1 ? "permit" : "permits"} across {properties.length}{" "}
              {properties.length === 1 ? "property" : "properties"}
            </Typography>
          )}
        </Box>

        <Box sx={{ px: { xs: 0, md: 2 }, maxWidth: 900, mx: "auto", pb: 6 }}>
          {/* Loading */}
          {loading && (
            <Stack spacing={2}>
              {[0, 1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={96}
                  sx={{ borderRadius: "12px" }}
                />
              ))}
            </Stack>
          )}

          {/* Error */}
          {!loading && error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => load()}>
                  Try again
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* Empty — no claimed properties at all */}
          {!loading && !error && properties.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                p: { xs: 4, md: 6 },
                border: "1px solid #e0e0e0",
                borderRadius: "16px",
                backgroundColor: "#f9fafb",
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                No home history yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                Claim your property to start building its history of records and
                permits.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={() => navigate("/")}
                sx={{ borderRadius: "8px", textTransform: "none" }}
              >
                Find your home
              </Button>
            </Box>
          )}

          {/* Empty — has properties but nothing confirmed yet */}
          {!loading && !error && properties.length > 0 && events.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                p: { xs: 4, md: 6 },
                border: "1px solid #e0e0e0",
                borderRadius: "16px",
                backgroundColor: "#f9fafb",
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Nothing on your timeline yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upload a contract or claim a matched permit from a property's
                Home History tab, and it'll appear here once confirmed.
              </Typography>
            </Box>
          )}

          {/* Populated */}
          {!loading && !error && events.length > 0 && (
            <>
              {/* Filters */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <ToggleButtonGroup
                  value={typeFilter}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setTypeFilter(v)}
                  aria-label="Filter by type"
                >
                  <ToggleButton value="all" sx={{ textTransform: "none", px: 2 }}>
                    All ({events.length})
                  </ToggleButton>
                  <ToggleButton value="record" sx={{ textTransform: "none", px: 2 }}>
                    Records ({recordCount})
                  </ToggleButton>
                  <ToggleButton value="permit" sx={{ textTransform: "none", px: 2 }}>
                    Permits ({permitCount})
                  </ToggleButton>
                </ToggleButtonGroup>

                {properties.length > 1 && (
                  <Select
                    value={propFilter}
                    size="small"
                    onChange={(e) => setPropFilter(e.target.value)}
                    sx={{ minWidth: 220, borderRadius: "8px" }}
                    aria-label="Filter by property"
                  >
                    <MenuItem value="all">All properties</MenuItem>
                    {properties.map((p) => (
                      <MenuItem key={p.propertyId} value={p.propertyId}>
                        {p.address}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Stack>

              {/* Filtered-empty */}
              {totalFiltered === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 4,
                    border: "1px dashed #e0e0e0",
                    borderRadius: "12px",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Nothing matches this filter.
                  </Typography>
                </Box>
              ) : (
                groups.map(([year, list], gi) => (
                  <Box key={year} sx={{ mb: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        letterSpacing: 1,
                        mb: 1.5,
                      }}
                    >
                      {year}
                    </Typography>
                    {list.map((e, i) => (
                      <EventRow
                        key={e.id}
                        event={e}
                        showProperty={propFilter === "all" && properties.length > 1}
                        onOpenProperty={openProperty}
                        // continuous rail except at the very last row overall
                        isLast={gi === groups.length - 1 && i === list.length - 1}
                      />
                    ))}
                  </Box>
                ))
              )}
            </>
          )}
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
};

export default HomeHistory;
